import { z } from "zod";
import { SORENESS_LEVELS, sessionReadiness } from "../training/workout";
import { agentPlanScope, agentPlanWeek, agentWriteRefusal, CATALOGUE_LOCALES, MAX_LOAD, MAX_NOTE_LENGTH, MAX_REPS, MAX_SET_DURATION_SECONDS, MAX_TARGET_SETS, } from "./data-access";
/**
 * The trainer's hands on the rest of the account (`D191`).
 *
 * `D73` says the agent writes, and until `D191` it could write a measurement,
 * a set, the profile and the config — and, on a plan with days, nothing but a
 * whole new draft. *"Add the dumbbell press to my plan"* rebuilt every day of
 * it. This file is the vocabulary that closes that gap: one line added,
 * removed, swapped, retargeted, restored or moved; a day added, renamed,
 * removed or rebuilt; the draft read, published or discarded; an exercise
 * excluded for good; a workout started and ended.
 *
 * ── THE SAME RULE AS EVERY OTHER SCHEMA ON THIS CHANNEL (`D72`) ─────────────
 * NO FIELD BELOW NAMES A PERSON OR A PLAN. The account is the `AgentActor`
 * the runtime injects; the plan is a WORD (`agentPlanScope`) the far side
 * resolves from that actor's subject. What the model does pass back is an
 * `itemId` or a `sessionId` from `getPlan` — and those are validated INSIDE
 * the resolved plan by the aggregate, so an id from somebody else's plan is
 * `plan.item_not_found` / `plan.session_not_found`, never a write. That is
 * the same pin `regeneratePlanSession` and `listSessionSets` already hold.
 *
 * ── EVERY WRITE IS `agent` (`D69`) ──────────────────────────────────────────
 * Stamped on the far side from `AGENT_ACTOR_PATH`, never from a request.
 */
const MAX_ID_LENGTH = 64;
const MAX_DAY_LABEL_LENGTH = 40;
const MAX_DAY_NAME_LENGTH = 80;
const MAX_REST_SECONDS = 600;
/** `D182`'s bound on a publish's refusals — the plan's own item ceiling. */
export const MAX_REJECT_CHANGE_IDS = 200;
export const MAX_EXCLUDE_ON_REBUILD = 20;
export const MAX_CANDIDATES = 20;
export const DEFAULT_CANDIDATES = 8;
const itemId = z
    .string()
    .min(1)
    .max(MAX_ID_LENGTH)
    .describe("An `itemId` from getPlan, read with the same `plan`.");
const sessionId = z
    .string()
    .min(1)
    .max(MAX_ID_LENGTH)
    .describe("A `sessionId` (a training day) from getPlan.");
const exerciseId = z
    .string()
    .min(1)
    .max(MAX_ID_LENGTH)
    .describe("A catalogue exercise id, from findExercises or getPlan.");
const calendarDay = z.iso.date();
const weekday = z.int().min(0).max(6).describe("0 = Sunday … 6 = Saturday.");
/** The targets a line may carry. `undefined` keeps, `null` clears. */
const targets = {
    notes: z.string().max(MAX_NOTE_LENGTH).nullable().optional(),
    restSeconds: z.int().min(0).max(MAX_REST_SECONDS).nullable().optional(),
    targetDurationSeconds: z
        .int()
        .min(1)
        .max(MAX_SET_DURATION_SECONDS)
        .nullable()
        .optional(),
    targetLoad: z.number().min(0).max(MAX_LOAD).nullable().optional(),
    targetRepsMax: z.int().min(1).max(MAX_REPS).nullable().optional(),
    targetRepsMin: z.int().min(1).max(MAX_REPS).nullable().optional(),
    targetSets: z.int().min(1).max(MAX_TARGET_SETS).nullable().optional(),
};
// ── PLAN LINES ──────────────────────────────────────────────────────────────
/** `addPlanExercise` — one exercise into one day. With no sets, reps or
 *  seconds the ENGINE prescribes it, exactly as the app's add does (`D182`). */
export const addPlanExerciseRequest = z.strictObject({
    /**
     * `true` only when the PERSON named this exercise (`D211`): an exercise
     * their saved equipment cannot do is refused otherwise, with
     * `plan.equipment_missing`.
     */
    allowMissingEquipment: z
        .boolean()
        .optional()
        .describe("true ONLY when the person asked for this exact exercise themselves; otherwise an exercise their equipment cannot do is refused."),
    exerciseId,
    plan: agentPlanScope.optional(),
    sessionId,
    ...targets,
});
export const removePlanExerciseRequest = z.strictObject({
    itemId,
    plan: agentPlanScope.optional(),
});
export const swapPlanExerciseRequest = z.strictObject({
    exerciseId,
    itemId,
    plan: agentPlanScope.optional(),
});
export const changePlanExerciseRequest = z.strictObject({
    itemId,
    plan: agentPlanScope.optional(),
    ...targets,
});
/** The undo (`D182`): the removed line back, and with `replacing` the line a
 *  swap or a change put in its slot taken out again. */
export const restorePlanExerciseRequest = z.strictObject({
    itemId: itemId.describe("The `removedItemId` a remove, swap or change returned."),
    plan: agentPlanScope.optional(),
    replacing: itemId
        .describe("For undoing a swap or a change: the `itemId` that write returned.")
        .optional(),
});
export const reorderPlanDayRequest = z.strictObject({
    itemIds: z
        .array(itemId)
        .min(1)
        .max(MAX_TARGET_SETS)
        .describe("EVERY itemId of that day, in the new order."),
    plan: agentPlanScope.optional(),
    sessionId,
});
/** The most lines one superset holds (`MAX_SUPERSET_SIZE`, `D246`). */
const MAX_SUPERSET_SIZE = 4;
/**
 * `setPlanSuperset` (`D246`): lines of ONE day that stand next to each other
 * performed as a superset — in rounds, the rest after the round — or taken
 * out of theirs. The ids are looked up inside the plan the far side resolved
 * (`D72`, `D191`), and the engine refuses a group that is not 2–4 adjacent
 * lines of one day, as it refuses the person's own tap.
 */
export const setPlanSupersetRequest = z.strictObject({
    grouped: z
        .boolean()
        .describe("true groups the lines; false takes them out of their group."),
    itemIds: z
        .array(itemId)
        .min(1)
        .max(MAX_SUPERSET_SIZE)
        .describe("Grouping: 2–4 lines of ONE day that stand next to each other. Ungrouping: the lines to take out."),
    plan: agentPlanScope.optional(),
});
// ── PLAN DAYS ───────────────────────────────────────────────────────────────
export const addPlanDayRequest = z.strictObject({
    label: z.string().trim().min(1).max(MAX_DAY_LABEL_LENGTH),
    plan: agentPlanScope.optional(),
    weekday: weekday.nullable().optional(),
});
export const updatePlanDayRequest = z.strictObject({
    label: z.string().trim().min(1).max(MAX_DAY_LABEL_LENGTH).optional(),
    name: z.string().trim().min(1).max(MAX_DAY_NAME_LENGTH).nullable().optional(),
    plan: agentPlanScope.optional(),
    sessionId,
    weekday: weekday.nullable().optional(),
});
export const removePlanDayRequest = z.strictObject({
    plan: agentPlanScope.optional(),
    sessionId,
});
/** The most days one plan holds (`MAX_SESSIONS_PER_PLAN` in the aggregate). */
const MAX_PLAN_DAYS = 14;
/**
 * `reorderPlanDays` (`D210`): the plan's DAYS in a new order — every
 * `sessionId` of that plan exactly once. The ids are looked up inside the plan
 * the far side resolved from `plan` (`D72`, `D191`); one this plan does not
 * show refuses the whole reorder.
 */
/**
 * What the agent's write wrapper adds to an INSERTING write, beside the
 * model's request and never inside it (`D210`).
 *
 * `clientIds` are derived from the turn, the call and its occurrence, so the
 * same call in a turn re-run after an eviction carries the same ids, and the
 * use case's replay-by-id answers the row the first run wrote instead of a
 * second one. The first id is the write's own row; the second, a follow-up
 * row the same tool writes (`addPlanExercise`'s load or rest on an engine
 * prescription). They are not in any tool's input schema: a model never
 * picks a row id.
 */
export const agentWriteOptions = z
    .strictObject({
    clientIds: z.array(z.uuid()).max(2).optional(),
})
    .optional();
export const reorderPlanDaysRequest = z.strictObject({
    plan: agentPlanScope.optional(),
    sessionIds: z
        .array(sessionId)
        .min(1)
        .max(MAX_PLAN_DAYS)
        .describe("EVERY sessionId of the plan, in the new order."),
});
/** `clearPlan` (`D210`): every day of the plan, and its exercises, off it —
 *  one write. */
export const clearPlanRequest = z.strictObject({
    plan: agentPlanScope.optional(),
});
/** `regeneratePlanDay` — the engine rebuilds ONE day of the plan in effect. */
export const regeneratePlanDayRequest = z.strictObject({
    excludeExerciseIds: z
        .array(exerciseId)
        .max(MAX_EXCLUDE_ON_REBUILD)
        .optional(),
    sessionId,
});
/** One write to the plan, answered. Every field but `ok` and `scope` is the
 *  address the next call — an undo above all — needs. */
export const agentPlanEditResult = z.discriminatedUnion("ok", [
    z.object({
        /** `false` when the write found the plan already as asked. */
        changed: z.boolean(),
        /** The line this write created or brought back. */
        itemId: z.string().optional(),
        ok: z.literal(true),
        /** The line this write took out — what `restorePlanExercise` takes. */
        removedItemId: z.string().optional(),
        scope: agentPlanScope,
        /** The day it happened in, or the day created. */
        sessionId: z.string().optional(),
        /** After `restartPlanWeeks`: where the plan now is in its weeks
         *  (`D211`), so the trainer can say "semana 1 de 4 a partir de hoje". */
        week: agentPlanWeek.optional(),
    }),
    agentWriteRefusal,
]);
// ── THE DRAFT ───────────────────────────────────────────────────────────────
export const getDraftChangesRequest = z.strictObject({
    locale: z.enum(CATALOGUE_LOCALES).optional(),
});
const draftLine = z.object({
    exerciseId: z.string(),
    exerciseName: z.string().nullable(),
    itemId: z.string(),
    restSeconds: z.number().nullable(),
    targetDurationSeconds: z.number().nullable(),
    targetLoad: z.number().nullable(),
    targetRepsMax: z.number().nullable(),
    targetRepsMin: z.number().nullable(),
    targetSets: z.number().nullable(),
});
export const agentDraftChangesResult = z.object({
    /** Each difference between the draft and the plan in effect. */
    changes: z.array(z.object({
        after: draftLine.nullable(),
        before: draftLine.nullable(),
        /** What `publishPlanDraft`'s `rejectChangeIds` takes. */
        changeId: z.string(),
        kind: z.enum(["added", "removed", "swapped", "changed"]),
        sessionLabel: z.string(),
    })),
    /** `false` when there is no draft — nothing to review. */
    hasDraft: z.boolean(),
});
export const discardPlanDraftRequest = z.strictObject({});
export const publishPlanDraftRequest = z.strictObject({
    rejectChangeIds: z
        .array(z.string().min(1).max(MAX_ID_LENGTH))
        .max(MAX_REJECT_CHANGE_IDS)
        .optional()
        .describe("Draft changes the person refused, from getDraftChanges — they are undone before the draft takes effect."),
});
export const dismissPlanSuggestionRequest = z.strictObject({});
// ── EXCLUSIONS ("não recomendar mais") ─────────────────────────────────────
export const excludeExerciseRequest = z.strictObject({ exerciseId });
export const includeExerciseRequest = z.strictObject({ exerciseId });
export const listExclusionsRequest = z.strictObject({
    locale: z.enum(CATALOGUE_LOCALES).optional(),
});
export const agentExclusionsResult = z.object({
    exclusions: z.array(z.object({
        excludedAt: z.string(),
        exerciseId: z.string(),
        name: z.string(),
    })),
    /**
     * The soft half (`D200`): favourites, and "Gosto deste" / "Prefiro evitar".
     * `prefer` ranks an exercise ahead of its equals and `avoid` behind them;
     * neither excludes. OPTIONAL on the result only — the agent deploys seconds
     * ahead of the API (`T-113`), and an `apps/ai` that required it would fail
     * every parse in that window. `apps/api` always sends it.
     */
    preferences: z
        .array(z.object({
        exerciseId: z.string(),
        favorite: z.boolean(),
        name: z.string().optional(),
        preference: z.enum(["prefer", "avoid"]).nullable(),
    }))
        .optional(),
});
export const agentExclusionWriteResult = z.discriminatedUnion("ok", [
    z.object({
        /** `false` when it was already as asked. */
        changed: z.boolean(),
        exerciseId: z.string(),
        ok: z.literal(true),
    }),
    agentWriteRefusal,
]);
// ── SWAP CANDIDATES ─────────────────────────────────────────────────────────
/** The ranked replacements for one line — the same feed the app's swap
 *  picker shows: the person's equipment, injuries and exclusions applied. */
export const findSwapCandidatesRequest = z.strictObject({
    itemId,
    limit: z.int().min(1).max(MAX_CANDIDATES).optional(),
    locale: z.enum(CATALOGUE_LOCALES).optional(),
    plan: agentPlanScope.optional(),
});
export const agentSwapCandidatesResult = z.object({
    candidates: z.array(z.object({
        equipment: z.string(),
        exerciseId: z.string(),
        name: z.string(),
        primaryMuscle: z.string(),
        reasonKey: z.string(),
    })),
    more: z.boolean(),
});
// ── DAY CANDIDATES (`D197`) ─────────────────────────────────────────────────
/** How many muscles one `findDayCandidates` call may ask about. */
export const MAX_DAY_CANDIDATE_MUSCLES = 4;
/**
 * What could go INTO one day — the app's "Adicionar exercício" feed, per
 * muscle: exercises that TARGET it, filtered by the person's equipment,
 * injuries, exclusions and level, minus what the day already holds. For the
 * person building a day by hand who asks "what could I add here?".
 */
export const findDayCandidatesRequest = z.strictObject({
    limit: z
        .int()
        .min(1)
        .max(MAX_CANDIDATES)
        .describe("Per muscle. Default 5.")
        .optional(),
    locale: z.enum(CATALOGUE_LOCALES).optional(),
    muscles: z
        .array(z.string().trim().min(1).max(60))
        .min(1)
        .max(MAX_DAY_CANDIDATE_MUSCLES)
        .describe('Muscle names in English or Portuguese ("chest", "Peitoral", "quadríceps"). Omitted: the day\'s own muscles, least trained in the week first.')
        .optional(),
    plan: agentPlanScope.optional(),
    sessionId,
});
export const agentDayCandidatesResult = z.object({
    /** One group per muscle asked about (or chosen), in that order. */
    groups: z.array(z.object({
        candidates: z.array(z.object({
            equipment: z.string(),
            exerciseId: z.string(),
            name: z.string(),
            primaryMuscle: z.string(),
        })),
        more: z.boolean(),
        muscle: z.string(),
        /** Why the list is empty when it is — equipment, injury, catalogue. */
        exhaustedReasonKey: z.string().nullable(),
    })),
    /** The muscle names the catalogue knows, when one asked for matched none
     *  or the day had no muscle to start from — ask again with one of these. */
    knownMuscles: z.array(z.string()).optional(),
    /** Names in `muscles` that matched no muscle of the catalogue. */
    unmatched: z.array(z.string()),
});
// ── STATS AND WORKOUTS ──────────────────────────────────────────────────────
export const getTrainingStatsRequest = z.strictObject({});
export const agentTrainingStatsResult = z.object({
    freezeAvailable: z.boolean(),
    milestones: z.number(),
    streakWeeks: z.number(),
    thisWeek: z.object({ done: z.number(), scheduled: z.number() }),
    totalSessions: z.number(),
});
/** How far back `getWorkoutStatus` may list workouts, in days. */
export const MAX_WORKOUT_LOOKBACK_DAYS = 366;
/** How many workouts one `deleteWorkouts` call may take out. */
export const MAX_WORKOUTS_PER_DELETE = 60;
export const getWorkoutStatusRequest = z.strictObject({
    /** `D198`: list every workout of the last N days, ids included — what
     *  `deleteWorkouts` takes. Omitted: the last 30 days, the newest five. */
    days: z
        .int()
        .min(1)
        .max(MAX_WORKOUT_LOOKBACK_DAYS)
        .describe("List EVERY workout of the last N days instead of the newest five.")
        .optional(),
});
const workoutRow = z.object({
    endedAt: z.string().nullable(),
    /** How many sets are logged in it. */
    sets: z.number(),
    startedAt: z.string(),
    /** `D198`: the workout's own id, what `deleteWorkouts` takes. Optional
     *  only for the rollout: an `apps/ai` newer than `apps/api` parses an
     *  answer without it. */
    workoutId: z.string().optional(),
});
export const agentWorkoutStatusResult = z.object({
    /** The workout still open, or `null`. Ending it is `endWorkout`. */
    open: workoutRow.nullable(),
    /** The last few workouts — or every one of the asked `days` — newest
     *  first. */
    recent: z.array(workoutRow),
});
// ── HISTORY AND THE WEEK COUNT (`D198`) ────────────────────────────────────
/** `deleteWorkouts` — finished workouts out of the person's history, softly:
 *  they stop counting in the streak, the progress and the summaries. */
export const deleteWorkoutsRequest = z.strictObject({
    workoutIds: z
        .array(z.string().min(1).max(MAX_ID_LENGTH))
        .min(1)
        .max(MAX_WORKOUTS_PER_DELETE)
        .describe("The `workoutId`s from getWorkoutStatus."),
});
export const agentDeleteWorkoutsResult = z.discriminatedUnion("ok", [
    z.object({
        /** How many workouts this call took out. */
        deleted: z.number(),
        ok: z.literal(true),
        /** Ids that were not a finished workout of this person (already
         *  deleted, open, or never theirs). */
        skipped: z.array(z.string()),
    }),
    agentWriteRefusal,
]);
/** `restartPlanWeeks` — week 1 of the plan in effect starts today. */
export const restartPlanWeeksRequest = z.strictObject({});
/**
 * `deleteMeasurement` — one point of a measurement series, named by its
 * series and its instant exactly as listMeasurements shows them. Not by id:
 * no tool here takes an id naming a person's data (`D72`), and the point is
 * looked up in the conversation subject's own series.
 */
export const deleteMeasurementRequest = z.strictObject({
    kind: z.string().min(1).max(40).describe("The series, e.g. `weight`."),
    measuredAt: z.iso
        .datetime()
        .describe("The point's `measuredAt`, copied from listMeasurements."),
});
export const agentDeleteResult = z.discriminatedUnion("ok", [
    z.object({
        /** `false` when it was already gone. */
        changed: z.boolean(),
        ok: z.literal(true),
    }),
    agentWriteRefusal,
]);
/** `startWorkout` — open a workout, optionally for a day of the plan, so the
 *  sets logged into it count for that day's progression. */
export const startWorkoutRequest = z.strictObject({
    /** How the person slept and how stressed they are, when they said so
     *  (`D214`) — the readiness check the app asks before a workout. */
    readiness: sessionReadiness
        .describe("Only what the person said about sleep and stress today.")
        .optional(),
    sessionId: sessionId
        .describe("The plan day being trained, from getPlan.")
        .optional(),
    startedAt: calendarDay
        .describe("YYYY-MM-DD for a workout done on an earlier day.")
        .optional(),
});
/** `endWorkout` — close the open workout, as the app's "Finalizar" does. */
export const endWorkoutRequest = z.strictObject({
    endedAt: calendarDay
        .describe("YYYY-MM-DD, only for a workout done on an earlier day.")
        .optional(),
    /**
     * How it ended (`D42`, `D214`). `abandoned` — left open, not really done —
     * keeps the sets but advances no plan and promises no summary or check-in
     * (`D177`); `complete` and `early` end it like the app's "Finalizar".
     */
    reason: z
        .enum(["complete", "early", "abandoned"])
        .describe("abandoned = the person did not really train it; it advances nothing.")
        .optional(),
});
// ── THE WORKOUT'S SETS, ITS FEEDBACK, AND WHAT ELSE THE APP DOES (`D214`) ───
const workoutId = z
    .string()
    .min(1)
    .max(MAX_ID_LENGTH)
    .describe("A `workoutId` from getWorkoutStatus.");
const setId = z
    .string()
    .min(1)
    .max(MAX_ID_LENGTH)
    .describe("A `setId` from listWorkoutSets.");
/** `listWorkoutSets` — one workout's sets, for a correction or a review. */
export const listWorkoutSetsRequest = z.strictObject({
    locale: z.enum(CATALOGUE_LOCALES).optional(),
    workoutId,
});
export const agentWorkoutSetsResult = z.object({
    sets: z.array(z.object({
        durationSeconds: z.number().nullable(),
        exerciseId: z.string(),
        exerciseName: z.string().nullable(),
        load: z.number().nullable(),
        performedAt: z.string(),
        reps: z.number().nullable(),
        rpe: z.number().nullable(),
        setId: z.string(),
    })),
});
/** `correctWorkoutSet` — the numbers of ONE logged set, as the app's edit
 *  does. Send only what changes; null clears a value. */
export const correctWorkoutSetRequest = z.strictObject({
    durationSeconds: z
        .int()
        .min(0)
        .max(MAX_SET_DURATION_SECONDS)
        .nullable()
        .optional(),
    load: z.number().min(0).max(MAX_LOAD).nullable().optional(),
    reps: z.int().min(0).max(MAX_REPS).nullable().optional(),
    rpe: z.number().min(1).max(10).nullable().optional(),
    setId,
});
/** `deleteWorkoutSet` — one logged set off its workout. */
export const deleteWorkoutSetRequest = z.strictObject({ setId });
export const agentSetWriteResult = z.discriminatedUnion("ok", [
    z.object({
        changed: z.boolean(),
        ok: z.literal(true),
        /** A correction's NEW row — every later reference uses it. */
        setId: z.string().optional(),
    }),
    agentWriteRefusal,
]);
/**
 * `recordSessionFeedback` — how a finished workout felt, as the app's
 * after-workout card asks (`D214`). NEVER pain: `painDuring` is screen-only
 * (`D149`, `D21`), so it is not a field here and the far side writes none.
 */
export const recordSessionFeedbackRequest = z.strictObject({
    difficulty: z.int().min(1).max(5),
    durationFit: z.enum(["ok", "too_long", "too_short"]),
    energyAfter: z.int().min(1).max(5),
    workoutId,
});
/** `recordSessionSoreness` — the next-morning check-in's overall answer. */
export const recordSessionSorenessRequest = z.strictObject({
    overall: z.enum(SORENESS_LEVELS),
    workoutId,
});
/** `movePlanExercise` — one line to ANOTHER day of the same plan, kept
 *  whole: its numbers, its reason, its history. */
export const movePlanExerciseRequest = z.strictObject({
    itemId,
    plan: agentPlanScope.optional(),
    toSessionId: sessionId.describe("The day to move it to, from getPlan."),
});
/** `getPlanChanges` — what changed on the person's account recently, newest
 *  first: the app's change feed, for "what did you change yesterday?". */
export const getPlanChangesRequest = z.strictObject({
    limit: z.int().min(1).max(30).optional(),
});
/** `getRetrospective` / `getInsight` — the app's weekly retrospective and
 *  its insight card, for the person in scope. */
export const getRetrospectiveRequest = z.strictObject({
    locale: z.enum(CATALOGUE_LOCALES).optional(),
});
export const getInsightRequest = z.strictObject({
    locale: z.enum(CATALOGUE_LOCALES).optional(),
});
/** A read the app already renders, passed through as JSON (`D214`). */
export const agentReadResult = z.object({ data: z.unknown() });
export const agentWorkoutWriteResult = z.discriminatedUnion("ok", [
    z.object({
        /** `false` when it was already as asked (a workout already open, or
         *  none to end). */
        changed: z.boolean(),
        /** On an end: the engine's reasons for what changes next time. */
        nextSessionChanges: z.array(z.string()).optional(),
        ok: z.literal(true),
    }),
    agentWriteRefusal,
]);
//# sourceMappingURL=account-tools.js.map